import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useUpdateNISParticipantStatus } from "../../../../../../api/services/nisParticipants/nisParticipantsApis";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
  StatusBadgeType,
} from "../../../../../../components";
import { MAX_LENGTH_1000, PAGE_IDS } from "../../../../../../helpers/constants";
import { isNotEmpty } from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../redux/slices/appState/appStateSlice";
import { ChangeNISStatusFormKey } from "../../../../modelsAndSchemas/Models";
import { createNISParticipantNISStatusSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ChangeNISStatusWrapper } from "./ChangeNISStatus.style";
import { getStatusOptionsMapping } from "./helper";

interface ChangeNISStatusProps {
  open: boolean;
  onClose: () => void;
  nisParticipantId: string;
  currentNISStatusId: StatusBadgeType;
  onNISStatusUpdated?: () => void;
}

const pageId = PAGE_IDS.CHANGE_NIS_STATUS;

export const ChangeNISStatus: FC<ChangeNISStatusProps> = ({
  open,
  onClose,
  nisParticipantId,
  currentNISStatusId,
  onNISStatusUpdated,
}) => {
  const { commonLabels, useFetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const [updatedStatusOptions, setUpdatedStatusOptions] = useState<
    OptionsDataType[]
  >([]);

  const {
    nisStatusTitle,
    changeStatusPopupHeader,
    notesPopupLabel,
    statusPopupDropdownLabel,
    nisParticipantStatusList,
  } = createNISParticipantNISStatusSiteCoreKeys;
  const getLabel = useFetchLabel(nisStatusTitle);

  const {
    mutateAsync: updateNISParticipantStatusMutation,
    isPending: isUpdateNISParticipantStatusPending,
    isError: isUpdateNISParticipantStatusError,
    isSuccess: isUpdateNISParticipantStatusSuccess,
  } = useUpdateNISParticipantStatus();

  const methods = useForm({
    defaultValues: {
      [ChangeNISStatusFormKey.status]: "",
      [ChangeNISStatusFormKey.notes]: "",
    },
  });

  const { control, getValues, setValue } = methods;

  useEffect(() => {
    const statusList = fetchDropdownValues(
      nisStatusTitle,
      nisParticipantStatusList,
    );
    const { defaultStatus, possibleValues } = getStatusOptionsMapping(
      currentNISStatusId,
      statusList,
    );
    if (defaultStatus) {
      setValue(ChangeNISStatusFormKey.status, defaultStatus?.value as string);
    }
    if (isNotEmpty(possibleValues)) {
      setUpdatedStatusOptions(possibleValues);
    }
  }, [currentNISStatusId]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isUpdateNISParticipantStatus: {
          isVisible: isUpdateNISParticipantStatusPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({ isUpdateNISParticipantStatus: { isVisible: false } }),
      );
    };
  }, [isUpdateNISParticipantStatusPending]);

  useEffect(() => {
    if (isUpdateNISParticipantStatusError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isUpdateNISParticipantStatusError]);

  useEffect(() => {
    if (isUpdateNISParticipantStatusSuccess) {
      onNISStatusUpdated?.();
    }
  }, [isUpdateNISParticipantStatusSuccess]);

  const handlePrimarySaveButtonClick = () => {
    const selectedStatus = getValues(ChangeNISStatusFormKey.status);
    if (isNotEmpty(nisParticipantId) && selectedStatus) {
      updateNISParticipantStatusMutation({
        nisParticipantId: nisParticipantId,
        statusId: selectedStatus,
        notes: getValues(ChangeNISStatusFormKey.notes),
      });
    }
  };

  return (
    <MuiModal
      onClose={onClose}
      title={getLabel(changeStatusPopupHeader)}
      open={open}
      primaryButtonTitle={commonLabels.save}
      secondaryButtonTitle={commonLabels.close}
      hasLargeContent
      onSecondaryButtonClick={onClose}
      onPrimaryButtonClick={handlePrimarySaveButtonClick}
    >
      <ChangeNISStatusWrapper>
        <MuiBox className="form-container status-dropdown">
          <MuiGrid container data-testid="change-status-modal">
            <MuiGrid size={{ md: 4, sm: 6, xs: 12 }} className="form-field">
              <FormController
                control={control}
                name={ChangeNISStatusFormKey.status}
              >
                <MuiSelect
                  label={getLabel(statusPopupDropdownLabel)}
                  options={updatedStatusOptions}
                  id={`${pageId}-${ChangeNISStatusFormKey.status}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  searchPlaceholder={commonLabels.search}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container>
            <MuiGrid size={{ md: 8, sm: 8, xs: 12 }} className="status-field">
              <FormController
                control={control}
                name={ChangeNISStatusFormKey.notes}
              >
                <MuiTextField
                  id={`${pageId}-${ChangeNISStatusFormKey.notes}`}
                  label={getLabel(notesPopupLabel)}
                  placeholder={commonLabels.inputPlaceHolder}
                  isTextarea
                  multiline
                  rows={4}
                  maxLength={MAX_LENGTH_1000}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </ChangeNISStatusWrapper>
    </MuiModal>
  );
};
